/* ============================================================================
   Keiki — site-wide behaviour that isn't owned by one section.
   Loaded from layout/theme.liquid.
   ========================================================================= */

/* ------------------------------------------ KLAVIYO "ADDED TO CART" EVENT ---
   Klaviyo's onsite script hooks Shopify's standard add-to-cart form submit.
   This theme adds via fetch() from a custom drawer, so that hook never fires
   and the event never reaches Klaviyo — silently disabling any flow triggered
   on Added to Cart. Confirmed empirically: instrumented fetch/sendBeacon/XHR,
   added an item, captured zero Klaviyo calls.

   Only the client-side event is affected. Started Checkout, Placed Order and
   profile sync all arrive over Shopify's webhooks and were never at risk.

   IMPLEMENTATION NOTE — do not "simplify" this back to wrapping window.fetch.
   That was tried first and does not work here: another script on the page
   captures a reference to fetch before this file runs and calls that captured
   reference, so a wrapper installed here is orphaned and never sees the
   request. Verified — the patch was installed, /cart/add.js fired, handler
   never ran.

   The theme dispatches its own events on document, which is both more robust
   and cheaper:
       product-ajax:added  ->  detail.product = the added line item
       cart:updated        ->  detail.cart    = the full cart

   Payload follows Klaviyo's documented Added to Cart schema so their prebuilt
   abandoned-cart templates read it without remapping.
   ========================================================================= */
(function () {
  'use strict';

  if (window.__keikiKlaviyoCartHook) return;
  window.__keikiKlaviyoCartHook = true;

  var latestCart = null; // kept fresh by cart:updated, saves a round trip

  function whenKlaviyoReady(cb, tries) {
    tries = tries || 0;
    if (window.klaviyo && typeof window.klaviyo.track === 'function') return cb();
    if (tries > 40) return; // ~20s, then give up quietly
    setTimeout(function () { whenKlaviyoReady(cb, tries + 1); }, 500);
  }

  function toMoney(cents) { return (cents || 0) / 100; }

  function absolute(url) {
    if (!url) return '';
    if (/^https?:/.test(url)) return url;
    if (url.indexOf('//') === 0) return window.location.protocol + url;
    return window.location.origin + url;
  }

  function lineItem(i) {
    return {
      ProductID: i.product_id,
      VariantID: i.variant_id,
      ProductName: i.product_title,
      VariantName: i.variant_title || '',
      Quantity: i.quantity,
      ItemPrice: toMoney(i.price),
      RowTotal: toMoney(i.line_price),
      ProductURL: absolute(i.url),
      ImageURL: absolute(i.image),
      SKU: i.sku || ''
    };
  }

  function send(added, cart) {
    whenKlaviyoReady(function () {
      try {
        var items = (cart && cart.items) || [];
        window.klaviyo.track('Added to Cart', {
          $value: toMoney(cart && cart.total_price),
          AddedItemProductName: added.product_title || added.title || '',
          AddedItemProductID: added.product_id,
          AddedItemVariantID: added.variant_id,
          AddedItemSKU: added.sku || '',
          AddedItemImageURL: absolute(added.image),
          AddedItemURL: absolute(added.url),
          AddedItemPrice: toMoney(added.price),
          AddedItemQuantity: added.quantity || 1,
          ItemNames: items.map(function (i) { return i.product_title; }),
          CheckoutURL: window.location.origin + '/cart',
          Items: items.map(lineItem)
        });
      } catch (e) { /* never let tracking break the cart */ }
    });
  }

  function trackAdded(added) {
    if (latestCart) return send(added, latestCart);
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { send(added, cart); })
      .catch(function () { send(added, { items: [], total_price: added.line_price }); });
  }

  document.addEventListener('cart:updated', function (e) {
    if (e && e.detail && e.detail.cart) latestCart = e.detail.cart;
  });

  document.addEventListener('product-ajax:added', function (e) {
    var added = e && e.detail && e.detail.product;
    if (!added || !added.product_id) return;
    // cart:updated and product-ajax:added fire in the same tick; give the cart
    // a moment to land so $value and Items reflect the post-add state.
    setTimeout(function () { trackAdded(added); }, 60);
  });
})();

/* ----------------------------------------- HOVER TO OPEN THE CART DRAWER ---
   Desktop nicety: hovering the header cart opens the drawer after a short
   delay; clicking still navigates to /cart as before. The link's href is left
   untouched so keyboard users, middle-click and "open in new tab" all keep
   working — this only adds a pointer affordance on top.

   Guarded to real pointers via (hover: hover) and (pointer: fine). Touch
   devices synthesise hover on tap, which would pop the drawer open on the way
   to the cart page — the media query is what prevents that, so don't drop it.

   The drawer is <cart-drawer-component id="CartDrawer">, which exposes open()
   and close() on its prototype. open() expects an event — it calls
   preventDefault() on its argument — so calling it bare throws.
   ========================================================================= */
(function () {
  'use strict';

  // Deliberately unhurried. At 400ms the drawer still surprised people who were
  // only travelling toward the account or search icons; 700ms means you have to
  // actually settle on the cart before it opens.
  var OPEN_DELAY = 700;

  if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (window.location.pathname === '/cart') return; // already there, nothing to reveal

  function init() {
    var trigger = document.querySelector('a.knav__cart, a[href="/cart"][aria-label="Cart"]');
    var drawer = document.getElementById('CartDrawer');
    if (!trigger || !drawer || typeof drawer.open !== 'function') return;

    var openTimer = null;

    function cancel() { clearTimeout(openTimer); }

    // <cart-count> renders as "Total items in cart: 1 … 1" — a bare parseInt on
    // that yields NaN, not a number, so anything treating NaN as "empty" never
    // opens. Take the last integer in the text, and if we can't read one at all,
    // fail OPEN: the whole point of the interaction is to reveal the cart.
    function cartIsEmpty() {
      var el = document.querySelector('cart-count');
      if (!el) return false;
      var digits = (el.textContent || '').match(/\d+/g);
      if (!digits || !digits.length) return false;
      return Number(digits[digits.length - 1]) === 0;
    }

    function scheduleOpen() {
      cancel();
      openTimer = setTimeout(function () {
        if (cartIsEmpty()) return; // an empty drawer is a dead end
        var dialog = drawer.querySelector('dialog');
        if (dialog && dialog.open) return; // already showing
        // open() calls preventDefault() on its argument, so hand it a real event
        try { drawer.open(new Event('click')); } catch (e) {}
      }, OPEN_DELAY);
    }

    trigger.addEventListener('mouseenter', scheduleOpen);
    // Left before the delay elapsed — they were passing through, not aiming.
    trigger.addEventListener('mouseleave', cancel);
    // Clicking should go to /cart, not race the hover-open.
    trigger.addEventListener('click', cancel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
